import type {LibraryPreviewKind} from './chartLibraryPreview';

export interface DashboardLibraryItem {
  key: string;
  type: string;
  title: string;
  setting?: Record<string, unknown>;
  configurable?: boolean;
}

export const LIBRARY_GROUPS = [
  'load',
  'performance',
  'volume',
  'zones',
  'other',
] as const;
export const LIBRARY_TYPES: Record<
  string,
  {
    group: (typeof LIBRARY_GROUPS)[number];
    preview: LibraryPreviewKind;
  }
> = {
  pmc_report: {group: 'load', preview: 'pmc'},
  annual_monitoring: {group: 'load', preview: 'annual'},
  power_duration: {group: 'performance', preview: 'curve'},
  power_profile: {group: 'performance', preview: 'powerProfile'},
  running_profile: {group: 'performance', preview: 'running'},
  estimated_ftp: {group: 'performance', preview: 'ftp'},
  durability: {group: 'performance', preview: 'durability'},
  w_prime: {group: 'performance', preview: 'reserve'},
  periodic_chart: {group: 'volume', preview: 'bars'},
  fitness_summery_report: {group: 'volume', preview: 'donut'},
  fitness_summery_chart: {group: 'volume', preview: 'donut'},
  nutrition_chart: {group: 'volume', preview: 'bars'},
  time_in_zone: {group: 'zones', preview: 'zones'},
  readiness_trend: {group: 'load', preview: 'readiness'},
  readiness: {group: 'load', preview: 'readiness'},
  peak_trends: {group: 'performance', preview: 'peaks'},
  metrics_chart: {group: 'other', preview: 'metrics'},
};

/** Reconcile saved order with current cards: remove stale keys and append new cards. */
export function orderDashboardItems<T extends {key: string}>(
  items: readonly T[],
  saved: readonly string[],
): T[] {
  const byKey = new Map(items.map(item => [item.key, item]));
  const keys = [...new Set([...saved, ...items.map(item => item.key)])];
  return keys.flatMap(key => {
    const item = byKey.get(key);
    return item ? [item] : [];
  });
}

export function moveDashboardItem(
  keys: readonly string[],
  key: string,
  direction: -1 | 1,
): string[] {
  const next = [...keys],
    from = next.indexOf(key),
    to = from + direction;
  if (from < 0 || to < 0 || to >= next.length) return next;
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** Library configuration matching must distinguish metric and aggregation variants. */
export function libraryVariantMatches(
  type: string,
  a: Record<string, unknown> = {},
  b: Record<string, unknown> = {},
): boolean {
  const sports = (setting: Record<string, unknown>) => {
    const values = Array.isArray(setting.workout_types) ? setting.workout_types.map(Number) : [0];
    return values.length === 0 || values.includes(0) ? '0' : [...new Set(values)].sort((x,y) => x-y).join(',');
  };
  if (sports(a) !== sports(b)) return false;
  if (type === 'periodic_chart') {
    const x = a.workout_summery as Record<string, unknown> | undefined;
    const y = b.workout_summery as Record<string, unknown> | undefined;
    return (
      (x?.workout_summery_types ?? 'distance') ===
        (y?.workout_summery_types ?? 'distance') &&
      (x?.group_by ?? 'week') === (y?.group_by ?? 'week')
    );
  }
  if (type === 'time_in_zone')
    return (
      (a.zone_type ?? 'time_in_zone_hr') === (b.zone_type ?? 'time_in_zone_hr')
    );
  return true;
}
